import { useState, useEffect, useRef } from 'react';
import { Send, Loader2, WifiOff, Wifi } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { useEnhancedWebSocketChat } from '@/hooks/useEnhancedWebSocketChat';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import PageHeader from '@/components/ui/page-header';
import { ChatCompletionModal } from './ChatCompletionModal';
import { ChatSummaryEditor } from './ChatSummaryEditor';
import { finalizeSummary } from '@/api/chat';
import { useToast } from '@/hooks/use-toast';
import AdminJsonToggle from '@/components/AdminJsonToggle';
import type { Module } from '@/types';
import type { 
  ChatModuleData, 
  CompletionSignalMessage, 
  SummaryDraftMessage 
} from '@/types/chatCompletion';

interface DynamicChatModuleEnhancedProps {
  module: Module;
  onComplete?: () => void;
}

export default function DynamicChatModuleEnhanced({ module, onComplete }: DynamicChatModuleEnhancedProps) {
  const [inputMessage, setInputMessage] = useState('');
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [completionData, setCompletionData] = useState<CompletionSignalMessage['metadata'] | null>(null);
  const [showSummaryEditor, setShowSummaryEditor] = useState(false);
  const [summaryData, setSummaryData] = useState<SummaryDraftMessage['metadata'] | null>(null);
  const [localCompletionStage, setLocalCompletionStage] = useState('chatting');
  
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Parse module data for UI configuration
  const chatConfig: ChatModuleData = module.data ? JSON.parse(module.data) : {};
  const { 
    welcomeMessage, 
    suggestedPrompts, 
    minMessages = 0, 
    allowSkip = false,
    completionCriteria,
    summaryConfig 
  } = chatConfig;
  
  const { 
    messages, 
    sendMessage, 
    isConnected, 
    isLoading, 
    currentResponse,
    hasActiveResponse,
    isThinking,
    completionStage,
    isGeneratingSummary,
    requestSummary,
    dismissCompletion,
    completeWithoutSummary
  } = useEnhancedWebSocketChat({
    onCompletionSignal: (data) => {
      console.log('Received completion signal, localCompletionStage:', localCompletionStage);
      // Only show completion modal if we're not already generating a summary
      if (localCompletionStage === 'chatting') {
        setCompletionData(data);
        // Delay showing modal to let user read the last message
        setTimeout(() => {
          setShowCompletionModal(true);
        }, 2000); // 2 second delay
      }
    },
    onSummaryDraft: (data) => {
      setSummaryData(data);
      // Add a small delay before showing editor to ensure smooth transition
      setTimeout(() => {
        setShowSummaryEditor(true);
        setShowCompletionModal(false);
      }, 100);
    }
  });

  // Track progress based on message count
  const userMessageCount = messages.filter(m => m.fromUser).length;
  const canComplete = userMessageCount >= minMessages;

  const handleSendMessage = () => {
    if (inputMessage.trim() && isConnected && !hasActiveResponse) {
      sendMessage(inputMessage.trim());
      setInputMessage('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleSuggestedPrompt = (prompt: string) => {
    if (isConnected && !hasActiveResponse) {
      sendMessage(prompt);
    }
  };

  // Auto-scroll to bottom when new messages arrive or during streaming
  useEffect(() => {
    if (scrollAreaRef.current) {
      const scrollContainer = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      }
    }
  }, [messages, currentResponse]);

  // Auto-focus input when response completes
  useEffect(() => {
    if (!hasActiveResponse && inputRef.current && isConnected && !showSummaryEditor) {
      inputRef.current.focus();
    }
  }, [hasActiveResponse, isConnected, showSummaryEditor]);

  // Auto-resize textarea based on content
  useEffect(() => {
    const textarea = inputRef.current;
    if (textarea) {
      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = 'auto';
      // Set height to scrollHeight, with a max of 120px (about 5 lines)
      const newHeight = Math.min(textarea.scrollHeight, 120);
      textarea.style.height = `${newHeight}px`;
    }
  }, [inputMessage]);

  // Handle completion modal actions
  const handleReviewSummary = () => {
    // Keep modal open but show generating state
    setLocalCompletionStage('summarizing');
    requestSummary();
  };

  const handleContinueChatting = () => {
    dismissCompletion();
    setShowCompletionModal(false);
  };

  const handleCompleteWithoutSummary = async () => {
    completeWithoutSummary();
    setShowCompletionModal(false);
    if (onComplete) {
      onComplete();
    }
  };

  // Handle summary finalization
  const handleFinalizeSummary = async () => {
    if (!summaryData?.summaryId) return;
    
    try {
      await finalizeSummary(summaryData.summaryId);
      setShowSummaryEditor(false);
      toast({
        title: 'Summary Saved',
        description: 'Your conversation summary has been saved successfully.',
      });
      
      if (onComplete) {
        onComplete();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save summary. Please try again.',
        variant: 'destructive',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white pb-20 safe-area-inset">
        <PageHeader 
          title={module.name || "AI Coaching"}
          variant="title-with-exit"
          onExit={() => navigate('/updates')}
        />
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-primary" role="status" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white safe-area-inset">
      <AdminJsonToggle 
        data={{
          module,
          chatConfig,
          messages,
          userMessageCount,
          canComplete,
          isConnected,
          completionStage,
          localCompletionStage,
          completionData,
          summaryData
        }}
        label="Enhanced Chat Module Data"
      />
      <PageHeader 
        title={module.name || "AI Coaching"}
        variant="title-with-exit"
        onExit={() => navigate('/updates')}
      />
      
      <div className="px-4 py-6 h-[calc(100vh-120px)]">
        <div className="h-full flex flex-col bg-white rounded-lg shadow-sm border">
          {/* Connection Status Bar */}
          <div className={cn(
            "flex items-center justify-between px-4 py-2 border-b",
            isConnected ? "bg-green-50" : "bg-red-50"
          )}>
            <div className="flex items-center gap-2">
              {isConnected ? (
                <>
                  <Wifi className="h-4 w-4 text-green-600" />
                  <span className="text-sm text-green-600">Connected</span>
                </>
              ) : (
                <>
                  <WifiOff className="h-4 w-4 text-red-600" />
                  <span className="text-sm text-red-600">Connecting...</span>
                </>
              )}
            </div>
            <div className="flex items-center gap-2">
              {completionStage !== 'chatting' && (
                <span className="text-xs text-muted-foreground px-2 py-1 bg-secondary rounded">
                  {completionStage}
                </span>
              )}
              <span className="text-xs text-muted-foreground">
                AI Coach
              </span>
            </div>
          </div>

          {/* Messages Area */}
          <ScrollArea ref={scrollAreaRef} className="flex-1 p-4">
            <div className="space-y-4">
              {/* Show custom welcome message if provided and no messages yet */}
              {welcomeMessage && messages.length === 0 && (
                <div className="flex justify-start">
                  <div className="max-w-[80%] rounded-lg px-4 py-2 bg-muted">
                    <p className="text-base">{welcomeMessage}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {format(new Date(), 'h:mm a')}
                    </p>
                  </div>
                </div>
              )}

              {messages.map((message, index) => (
                <div
                  key={message.id || index}
                  className={cn(
                    "flex",
                    message.fromUser ? "justify-end" : "justify-start"
                  )}
                >
                  <div
                    className={cn(
                      "max-w-[80%] rounded-lg px-4 py-2",
                      message.fromUser
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted"
                    )}
                  >
                    <p className="text-base whitespace-pre-wrap">{message.text}</p>
                    <p className={cn(
                      "text-xs mt-1",
                      message.fromUser 
                        ? "text-primary-foreground/70" 
                        : "text-muted-foreground"
                    )}>
                      {format(new Date(message.timestamp), 'h:mm a')}
                    </p>
                  </div>
                </div>
              ))}

              {/* Current streaming response */}
              {currentResponse && (
                <div className="flex justify-start">
                  <div className="max-w-[80%] rounded-lg px-4 py-2 bg-muted">
                    <p className="text-base whitespace-pre-wrap">{currentResponse}</p>
                    <div className="flex items-center gap-1 mt-1">
                      <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                      <p className="text-xs text-muted-foreground">AI is typing...</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Thinking indicator */}
              {isThinking && !currentResponse && (
                <div className="flex justify-start">
                  <div className="max-w-[80%] rounded-lg px-4 py-2 bg-muted">
                    <div className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      <p className="text-base text-muted-foreground">
                        Thinking...
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>

          {/* Suggested Prompts */}
          {suggestedPrompts && suggestedPrompts.length > 0 && userMessageCount === 0 && (
            <div className="px-4 py-2 border-t border-b bg-gray-50">
              <p className="text-xs text-muted-foreground mb-2">Suggested topics:</p>
              <div className="flex flex-wrap gap-2">
                {suggestedPrompts.map((prompt, index) => (
                  <Button
                    key={index}
                    variant="outline"
                    size="sm"
                    onClick={() => handleSuggestedPrompt(prompt)}
                    disabled={!isConnected || hasActiveResponse || completionStage !== 'chatting'}
                    className="text-xs"
                  >
                    {prompt}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Input Area */}
          <div className="p-4 border-t">
            <div className="flex gap-2">
              <Textarea
                ref={inputRef}
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={
                  !isConnected ? "Connecting..." :
                  completionStage === 'summarizing' ? "Generating summary..." :
                  completionStage !== 'chatting' ? "Chat paused" :
                  "Type your message..."
                }
                disabled={!isConnected || hasActiveResponse || completionStage !== 'chatting'}
                className="flex-1 min-h-[40px] resize-none overflow-hidden"
                rows={1}
              />
              <Button
                onClick={handleSendMessage}
                disabled={!inputMessage.trim() || !isConnected || hasActiveResponse || completionStage !== 'chatting'}
                size="icon"
              >
                {hasActiveResponse ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </div>
            
          </div>

          {/* Progress and Completion */}
          {minMessages > 0 && completionStage === 'chatting' && (
            <div className="p-4 border-t">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-muted-foreground">
                  Messages sent: {userMessageCount} / {minMessages}
                </p>
                {canComplete && onComplete && (
                  <Button onClick={onComplete} variant="default" size="sm">
                    Continue
                  </Button>
                )}
                {allowSkip && !canComplete && onComplete && (
                  <Button onClick={onComplete} variant="outline" size="sm">
                    Skip for now
                  </Button>
                )}
              </div>
              {!canComplete && (
                <p className="text-xs text-muted-foreground">
                  Send {minMessages - userMessageCount} more message{minMessages - userMessageCount !== 1 ? 's' : ''} to continue
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Completion Modal */}
      {completionData && (
        <ChatCompletionModal
          isOpen={showCompletionModal}
          onClose={() => setShowCompletionModal(false)}
          completionData={completionData}
          onReviewSummary={handleReviewSummary}
          onContinue={handleContinueChatting}
          onComplete={handleCompleteWithoutSummary}
          isGeneratingSummary={isGeneratingSummary}
        />
      )}

      {/* Summary Editor Dialog */}
      {summaryData && (
        <Dialog open={showSummaryEditor} onOpenChange={setShowSummaryEditor}>
          <DialogContent className="max-w-4xl h-[90vh] p-0">
            <ChatSummaryEditor
              summaryId={summaryData.summaryId}
              initialContent={summaryData.content}
              onFinalize={handleFinalizeSummary}
              onClose={() => {
                setShowSummaryEditor(false);
                setLocalCompletionStage('chatting');
                dismissCompletion();
              }}
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}